import { bearingBetween } from './routeGeo'

export interface FlythroughKeyframe {
  label: string
  center: [number, number]
  zoom: number
  pitch: number
  bearing: number
  flyDurationMs: number
  lingerMs: number
}

export interface FlythroughWaypoint {
  name: string
  longitude: number
  latitude: number
  poiType?: string | null
}

function bboxCenter(wps: FlythroughWaypoint[]): [number, number] {
  const lngs = wps.map((w) => w.longitude)
  const lats = wps.map((w) => w.latitude)
  return [
    (Math.min(...lngs) + Math.max(...lngs)) / 2,
    (Math.min(...lats) + Math.max(...lats)) / 2,
  ]
}

function overviewZoom(wps: FlythroughWaypoint[]): number {
  const lngs = wps.map((w) => w.longitude)
  const lats = wps.map((w) => w.latitude)
  const spanLng = Math.max(...lngs) - Math.min(...lngs)
  const spanLat = Math.max(...lats) - Math.min(...lats)
  const span = Math.max(spanLng, spanLat)
  if (span < 0.01) return 14
  if (span < 0.05) return 13
  if (span < 0.2) return 11.5
  if (span < 0.5) return 10.5
  if (span < 1.5) return 9.5
  if (span < 4) return 8
  return 6.5
}

function zoomForPoi(poiType: string | null | undefined): number {
  switch (poiType) {
    case 'campsite': return 14.5
    case 'viewpoint': return 13.5
    case 'trailhead': return 13
    case 'meetup_spot': return 14
    default: return 13
  }
}

export function buildFlythroughSequence(waypoints: FlythroughWaypoint[]): FlythroughKeyframe[] {
  if (waypoints.length === 0) return []

  const overviewCenter = bboxCenter(waypoints)
  const ovZoom = overviewZoom(waypoints)

  const overviewFrame: FlythroughKeyframe = {
    label: 'Overview',
    center: overviewCenter,
    zoom: ovZoom,
    pitch: 30,
    bearing: 0,
    flyDurationMs: 2500,
    lingerMs: 1200,
  }

  const worldFrame: FlythroughKeyframe = {
    label: 'World',
    center: overviewCenter,
    zoom: 3,
    pitch: 0,
    bearing: 0,
    flyDurationMs: 0,
    lingerMs: 600,
  }

  const waypointFrames: FlythroughKeyframe[] = waypoints.map((wp, i) => {
    const prev = i === 0 ? overviewCenter : [waypoints[i - 1].longitude, waypoints[i - 1].latitude] as [number, number]
    const curr: [number, number] = [wp.longitude, wp.latitude]
    const bearing = bearingBetween(prev, curr)
    const zoom = zoomForPoi(wp.poiType)
    const pitch = wp.poiType === 'viewpoint' ? 65 : 55

    return {
      label: wp.name,
      center: curr,
      zoom,
      pitch,
      bearing,
      flyDurationMs: 2800,
      lingerMs: 2000,
    }
  })

  const outroFrame: FlythroughKeyframe = {
    ...overviewFrame,
    flyDurationMs: 2000,
    lingerMs: 800,
  }

  return [worldFrame, overviewFrame, ...waypointFrames, outroFrame]
}
